import { useEffect, useState } from 'react';

import type { ApolloError } from '@apollo/client';
import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

import submissionHeadersQuery from 'bundles/grading/api/SubmissionHeadersQuery.graphql';
import type {
  SubmissionHeadersQuery,
  SubmissionHeadersQueryVariables,
} from 'bundles/grading/api/__generated__/SubmissionHeadersQuery';
import type { SubmissionHeader } from 'bundles/grading/api/types';

export type UseSubmissionHeadersResult = {
  loading: boolean;
  data: SubmissionHeader[];
  error?: ApolloError;
};

/**
 * This hook sends a query to fetch a list of submission attempts
 */
const useSubmissionHeadersQuery = (variables: SubmissionHeadersQueryVariables): UseSubmissionHeadersResult => {
  const [headers, setHeaders] = useState<SubmissionHeader[]>([]);

  const submitterId = variables.gradingTaskFilter?.submitterIds?.[0];
  useEffect(() => {
    // Reset previously loaded headers when submitterId changes in the URL.
    // This way clicking "Submission history" button won't query AttemptData for
    // curr submitterId and attemptId from headers loaded for prev submitterId
    setHeaders([]);
  }, [submitterId]);

  const onCompleted = (data: SubmissionHeadersQuery) => {
    // Using the spread operator to make a (shallow) copy of the array. This way when we are sorting it,
    // the data in the Apollo cache are not changed.
    const submissionHeaders = [
      ...(data?.GroupGrading?.queryGroup?.items?.[0]?.gradingTasks?.[0]?.submissionHeaders || []),
    ] as SubmissionHeader[];

    // sort by attempt number - attempts with higher number first.
    submissionHeaders.sort((header1, header2) => header2.attemptNumber - header1.attemptNumber);

    setHeaders(submissionHeaders);
  };

  const result = useQuery<SubmissionHeadersQuery, SubmissionHeadersQueryVariables>(submissionHeadersQuery, {
    variables,
    context: { clientName: 'gatewayGql' },
    onCompleted,
    onError: (error) => {
      Sentry.captureException(error);
    },
  });

  return {
    loading: result.loading,
    data: headers,
    error: result.error,
  };
};

export default useSubmissionHeadersQuery;
